/**
 * How approved extra work is labelled on the job card and on the invoice.
 *
 * A line that came from extra work the customer accepted carries the request
 * it came from (invoice_line_items.source_quote_id → quotes). The invoice says
 * so in words the customer has already seen: the approval page calls it
 * "Extra work #N", and only the customer can accept it — through their own
 * link (fn_additional_work_decide) — so "approved by customer online" is a
 * statement of record, with the date it happened. That is what settles a
 * "I never agreed to that" before it starts.
 */

export interface ExtraWorkSource {
    quote_type?: string | null;
    sequence_no?: number | null;
    accepted_at?: string | null;
}

const LONDON_DATE = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
});

/** Short label for the job card: "Extra work #1 · approved". */
export function extraWorkCardLabel(sequenceNo: number): string {
    return `Extra work #${sequenceNo} · approved`;
}

/**
 * The note printed under an invoice line, or null when the line is ordinary
 * work (or the approval could not be read — then the invoice says nothing
 * rather than something unproven).
 */
export function extraWorkInvoiceNote(source: ExtraWorkSource | null | undefined): string | null {
    if (!source || source.quote_type !== 'additional_work' || !source.accepted_at) return null;
    const when = new Date(source.accepted_at);
    if (Number.isNaN(when.getTime())) return null;
    const ref = source.sequence_no != null ? `Extra work #${source.sequence_no}` : 'Extra work';
    return `${ref} · approved by customer online on ${LONDON_DATE.format(when)}`;
}
